import React, { useEffect, useState } from 'react';
import { Col, ListGroup, Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../config/ApiClient';
import { showError } from '../components/Alert';
import ForYouProducts from '../components/ForYouProducts';
import type { OrderDTO } from '../dto/order/OrderDTO';
import type { OrderItemDTO } from '../dto/order/OrderItemDTO';
import {
  calculateMarketShipping,
  getSelectedMarket,
} from '../config/marketConfig';
import '../styles/ShoppingCart.css';

const ShoppingCart = () => {
  const [cart, setCart] = useState<OrderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient
      .get<OrderDTO>('/orders/cart')
      .then((res) => setCart(res.data))
      .catch((err) =>
        showError(err.response?.data?.message || 'Failed to load cart')
      )
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (item: OrderItemDTO, newQty: number) => {
    if (newQty < 1) return;

    try {
      const res = await apiClient.post<OrderDTO>('/orders/cart/add', {
        itemId: item.itemId,
        itemOptionId: item.itemOptionId,
        quantity: 1, // always add 1 to increase
      });

      setCart((prev) => {
        if (!prev) return prev;
        const updatedItems = prev.items.map((i) =>
          i.itemId === item.itemId && i.itemOptionId === item.itemOptionId
            ? { ...i, quantity: newQty }
            : i
        );
        return { ...prev, items: updatedItems };
      });
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to update quantity');
    }
  };
  const handleRemove = async (item: OrderItemDTO, quantityToRemove: number) => {
    try {
      const res = await apiClient.delete<OrderDTO>('/orders/cart/remove', {
        data: {
          itemId: item.itemId,
          itemOptionId: item.itemOptionId,
          quantity: quantityToRemove,
        },
      });

      setCart((prev) => {
        if (!prev) return null;

        return {
          ...prev,
          items: prev.items
            .map((i) => {
              if (
                i.itemId === item.itemId &&
                i.itemOptionId === item.itemOptionId
              ) {
                const newQty = i.quantity - quantityToRemove;
                if (newQty <= 0) return null; // remove this item
                return { ...i, quantity: newQty };
              }
              return i;
            })
            .filter(Boolean) as OrderItemDTO[], // filter out nulls
        };
      });
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to update cart');
    }
  };

  const handleCheckout = async () => {
    try {
      const res = await apiClient.post('/orders/checkout', {
        successUrl: `${window.location.origin}/checkout/success`,
        cancelUrl: `${window.location.origin}/checkout/cancel`,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to start checkout');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="text-center my-5">
        <h3>Your cart is empty.</h3>
      </div>
    );
  }

  const currentTotal = cart.items.reduce(
    (sum, item) => sum + item.quantity * item.itemPrice,
    0
  );
  const market = getSelectedMarket();
  const currency = market.currency;
  const shipping = calculateMarketShipping(currentTotal, market);
  const orderTotal = currentTotal + shipping;
  const shippingLabel =
    shipping === 0
      ? 'Free'
      : `${currency} ${shipping.toFixed(2)}`;

  return (
    <>
      <div className="shopping-cart-container">
        <Col>
          <h2 className="shopping-bag-title">SHOPPING BAG ITEMS</h2>
          <ListGroup variant="flush">
            {cart.items.map((item, index) => (
              <ListGroup.Item key={index} className="cart-item flex-column">
                <div
                  style={{ width: '100%', height: 3, background: '#D9D9D9' }}
                  className="line"
                />
                <div className="img-details flex-start">
                  <img
                    src={item.thumbnail}
                    alt={item.itemName}
                    className="cart-img"
                  />
                  <div className="item-details flex-column">
                    <div className="title-price flex-row">
                      <div className="item-title">{item.itemName}</div>
                      <p className="item-price">
                        {item.currency} {item.itemPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="item-size">
                      Size: {item.size} / Color: {item.color}
                    </div>
                    <div className="split-qL flex-row">
                      <div className="item-quantity-control">
                        <Button
                          variant="light"
                          className="qty-btn"
                          onClick={() => handleRemove(item, 1)}
                        >
                          -
                        </Button>
                        <span className="mx-2">{item.quantity}</span>
                        <Button
                          variant="light"
                          className="qty-btn"
                          onClick={() => handleAdd(item, item.quantity + 1)}
                        >
                          +
                        </Button>
                      </div>
                      <div className="item-links">
                        <Button
                          variant="link"
                          onClick={() => handleRemove(item, item.quantity)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Col>
        <Col xs={12} md={4}>
          <Card className="order-card">
            <Card.Body className="order-card-body flex-column">
              <Card.Title className="order-card-title">
                ORDER SUMMARY
              </Card.Title>
              <div
                style={{ width: '100%', height: 3, background: '#D9D9D9' }}
                className="line"
              />
              <div className="d-flex flex-row">
                <Card.Text className="tot-ship-card">Total</Card.Text>
                <Card.Text className="order-card-total">
                  {currency} {currentTotal.toFixed(2)}
                </Card.Text>
              </div>
              <div className="d-flex flex-row">
                <Card.Text className="tot-ship-card">Total taxes</Card.Text>
                <Card.Text className="order-card-shipping">
                  Calculated at checkout
                </Card.Text>
              </div>
              <div className="d-flex flex-row">
                <Card.Text className="tot-ship-card">Shipping</Card.Text>
                <Card.Text className="order-card-shipping">
                  {shippingLabel}
                </Card.Text>
              </div>
              <div className="order-margin d-flex flex-row">
                <Card.Text className="order-total">Order Total</Card.Text>
                <Card.Text className="order-total-amt">
                  {currency} {orderTotal.toFixed(2)}
                </Card.Text>
              </div>

              <Button
                className="checkout-button"
                onClick={() => handleCheckout()}
              >
                Checkout
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <div className="cart-for-you">
          <ForYouProducts title="YOU MAY ALSO LIKE..." />
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;
